// Splash Screen Component - Welcome intro before login
import React from 'react';
import { Box, IconButton } from '@mui/joy';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

interface SplashScreenProps {
  onComplete: () => void;
}

export default function SplashScreen({ onComplete }: SplashScreenProps) {
  // Detect device type and select appropriate image
  const getResponsiveImage = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const aspectRatio = width / height;

    // Portrait devices (phones, tablets) - use 1080 x 1920
    if (aspectRatio < 1) {
      return '/activate-trial-1080x1920.png';
    }
    
    // Landscape devices (desktop)
    if (aspectRatio > 2 || width >= 2560) {
      // Ultrawide or large desktop - use 3440 x 1440
      return '/activate-trial-3440x1440.png';
    }
    
    // Standard desktop/laptop - use 1920 x 1080
    return '/activate-trial-1920x1080.png';
  };

  const [backgroundImage, setBackgroundImage] = React.useState(getResponsiveImage());

  // Update image on window resize
  React.useEffect(() => {
    const handleResize = () => {
      setBackgroundImage(getResponsiveImage());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        overflow: 'hidden',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <IconButton
        size="lg"
        variant="plain"
        color="neutral"
        onClick={onComplete}
        sx={{
          position: 'absolute',
          top: {
            xs: '62%', // below logo text on mobile with more spacing
            md: '50%', // vertically centered on desktop
          },
          left: {
            xs: '50%', // center on mobile/portrait
            md: 'auto', // use right positioning on desktop
          },
          right: {
            xs: 'auto',
            md: '12%', // 12% from right edge on desktop - leaving lots of space
          },
          transform: {
            xs: 'translate(-50%, -50%)', // center both axes on mobile
            md: 'translateY(-50%)', // only center vertically on desktop
          },
          zIndex: 1,
          width: { xs: 56, md: 64 },
          height: { xs: 56, md: 64 },
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#b8860b',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          backdropFilter: 'blur(16px)',
          border: '2px solid rgba(184, 134, 11, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
            boxShadow: '0 6px 24px rgba(184, 134, 11, 0.25)',
            transform: {
              xs: 'translate(-50%, -50%) scale(1.08)', // maintain center on mobile
              md: 'translateY(-50%) scale(1.08)', // just scale on desktop
            },
            border: '2px solid rgba(184, 134, 11, 0.5)',
          },
          '&:active': {
            transform: {
              xs: 'translate(-50%, -50%) scale(1.02)',
              md: 'translateY(-50%) scale(1.02)',
            },
          },
        }}
      >
        <ArrowForwardRoundedIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
      </IconButton>
    </Box>
  );
}
